import { Component, Input, OnInit, OnChanges, OnDestroy, Output, EventEmitter, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatasetService } from 'app/main/components/dataset/services/dataset.service';
import { WebSocketService } from 'app/services/websocket.service';
import { ISocketEvent } from 'app/interfaces';
import { ActualTripParticipantLogsDialogComponent } from './participant-logs-dialog.component';

/** Stato mail recensione mostrato in tabella: verde = partita, rossa = no (tooltip col motivo). */
interface IReviewMailState {
	sent: boolean;
	tooltip: string;
}

interface IParticipantRow {
	id: string;
	trip_booking_id: string;
	trip_booking_code: string;
	trip_booking_date: string;
	description: string;
	age_range_code: string;
	nation_code: string;
	lang_code: string;
	presence: number;
	last_presence_checked_at: string | null;
	note: string | null;
	trip_booking_participant_id: string;
	review_request_email_sent_at: string | null;
	review_request_email_status: string | null;
	_updating?: boolean;
	_reviewMail?: IReviewMailState;
}

/** modules.reviews.request_email_* del dominio della partenza (dal command/list). */
interface IReviewMailSettings {
	enabled: boolean;
	delay_hours: number;
	start_from: string | null;
}

interface IListResponse {
	items: IParticipantRow[];
	meta: { last: boolean; page: number; perPage: number; total: number };
	review_request_email?: IReviewMailSettings;
}

/** Finestra di retry del job lato server (CheckPendingReviewRequestEmails::MAX_AGE_DAYS). */
const REVIEW_MAIL_MAX_AGE_DAYS = 3;

@Component({
    selector: 'actual-trip-participants-tab',
    templateUrl: './participants-tab.component.html',
    styleUrls: ['./participants-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ActualTripParticipantsTabComponent implements OnInit, OnChanges, OnDestroy {

	@Input() actualTripId: string;
	@Input() canEdit = false;
	@Output() presenceChanged = new EventEmitter<void>();

	private _unsubscribeAll = new Subject<void>();
	private _socketHandler = (event: ISocketEvent) => this.onSocketEvent(event);

	loading = false;
	bulkLoading = false;
	rows: IParticipantRow[] = [];
	displayedColumns: string[] = [];
	selection = new SelectionModel<string>(true, []);
	reviewMailSettings: IReviewMailSettings = { enabled: false, delay_hours: 24, start_from: null };

	constructor(
		private datasetService: DatasetService,
		private snackBar: MatSnackBar,
		private webSocketService: WebSocketService,
		private dialog: MatDialog,
		private translateService: TranslateService,
	) {}

	ngOnInit(): void {
		this.recomputeColumns();
		if (this.actualTripId) this.load();
		if (this.webSocketService && this.webSocketService.ioSocket) {
			this.webSocketService.ioSocket.on('channel-event', this._socketHandler);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.canEdit) this.recomputeColumns();
		if (changes.actualTripId && !changes.actualTripId.firstChange) {
			this.selection.clear();
			this.load();
		}
	}

	ngOnDestroy(): void {
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
		if (this.webSocketService && this.webSocketService.ioSocket) {
			this.webSocketService.ioSocket.off('channel-event', this._socketHandler);
		}
	}

	private recomputeColumns(): void {
		const base = ['description', 'trip_booking_code', 'age_range_code', 'presence', 'last_presence_checked_at', 'note', 'logs'];
		// colonna stato mail recensione solo se la feature è attiva sul dominio della partenza
		if (this.reviewMailSettings.enabled) base.push('review_mail');
		this.displayedColumns = this.canEdit ? ['select', ...base] : base;
	}

	hasLogs(row: IParticipantRow): boolean {
		return !!row.id && row.id.indexOf('B-') !== 0;
	}

	openLogs(row: IParticipantRow): void {
		if (!this.hasLogs(row)) return;
		this.dialog.open(ActualTripParticipantLogsDialogComponent, {
			data: { participantId: row.id, participantDescription: row.description },
			minWidth: 520,
			maxWidth: '90vw'
		});
	}

	/**
	 * Stato icona mail recensione. review_request_email_sent_at è valorizzato dal job anche sugli
	 * skip (anti-retry), quindi il verde dipende da review_request_email_status, non dal timestamp.
	 * Con status assente e sent_at presente si tratta di righe elaborate prima dell'introduzione
	 * dello status (esito non registrato).
	 */
	private buildReviewMailState(row: IParticipantRow): IReviewMailState {
		const t = (text: string) => this.translateService.instant(text);
		const status = row.review_request_email_status;

		if (status === 'sent') {
			return {
				sent: true,
				tooltip: t('Richiesta di recensione inviata all\'intestatario della prenotazione il')
					+ ' ' + this.formatMoment(row.review_request_email_sent_at)
			};
		}
		if (status === 'skipped_no_email') {
			return { sent: false, tooltip: t('Non inviata: l\'intestatario della prenotazione non ha un indirizzo email') };
		}
		if (status === 'skipped_no_url') {
			return {
				sent: false,
				tooltip: t('Non inviata: link della pagina esperienza non disponibile (sito esterno non raggiungibile o esperienza non pubblicata)')
			};
		}
		if (status === 'skipped_no_data') {
			return { sent: false, tooltip: t('Non inviata: dati della prenotazione o della partenza incompleti') };
		}
		if (row.review_request_email_sent_at) {
			return {
				sent: true,
				tooltip: t('Richiesta di recensione elaborata il') + ' ' + this.formatMoment(row.review_request_email_sent_at)
					+ ' (' + t('esito non registrato') + ')'
			};
		}
		if (row.presence != 1) {
			return { sent: false, tooltip: t('Non inviata: la presenza del partecipante non è confermata') };
		}
		if (!row.last_presence_checked_at) {
			return { sent: false, tooltip: t('Non inviata: data del check-in non disponibile') };
		}

		const checkedAt = new Date(row.last_presence_checked_at).getTime();
		if (this.reviewMailSettings.start_from) {
			const startFrom = new Date(this.reviewMailSettings.start_from).getTime();
			if (checkedAt < startFrom) {
				return {
					sent: false,
					tooltip: t('Non inviata: il check-in è precedente alla data di attivazione delle richieste')
						+ ' (' + this.formatMoment(this.reviewMailSettings.start_from) + ')'
				};
			}
		}

		const dueAt = checkedAt + this.reviewMailSettings.delay_hours * 3600 * 1000;
		const now = Date.now();
		if (now < dueAt) {
			return {
				sent: false,
				tooltip: t('Non ancora inviata: invio previsto il') + ' ' + this.formatMoment(new Date(dueAt))
					+ ' (' + this.reviewMailSettings.delay_hours + ' ' + t('ore dopo il check-in') + ')'
			};
		}
		if (now > dueAt + REVIEW_MAIL_MAX_AGE_DAYS * 86400 * 1000) {
			return {
				sent: false,
				tooltip: t('Non inviata: finestra di invio scaduta (oltre 3 giorni dal momento previsto)')
			};
		}
		return {
			sent: false,
			tooltip: t('In attesa di invio: previsto dal') + ' ' + this.formatMoment(new Date(dueAt))
		};
	}

	private formatMoment(value: string | Date): string {
		if (!value) return '';
		return formatDate(value, 'dd/MM/yyyy HH:mm', 'it');
	}

	private onSocketEvent(event: ISocketEvent): void {
		if (!event || !event.data) return;
		if (event.data.datasetCode !== 'actual_trip_participants') return;
		if (event.data.payloadData && event.data.payloadData.actual_trip_id !== this.actualTripId) return;
		this.load(true);
	}

	load(silent = false): void {
		if (!this.actualTripId) return;
		if (!silent) this.loading = true;
		this.datasetService.post<IListResponse>('/dataset/actual_trip_participants/command/list', {
			actual_trip_id: this.actualTripId,
			perPage: 500
		})
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: res => {
					const items = (res && res.items) ? res.items : [];
					if (res && res.review_request_email) {
						this.reviewMailSettings = res.review_request_email;
						this.recomputeColumns();
					}
					this.rows = items;
					if (this.reviewMailSettings.enabled) {
						this.rows.forEach(row => row._reviewMail = this.buildReviewMailState(row));
					}
					this.loading = false;
					// keep only still-visible IDs in selection
					const visibleIds = new Set(this.rows.map(r => r.id));
					this.selection.selected
						.filter(id => !visibleIds.has(id))
						.forEach(id => this.selection.deselect(id));
				},
				error: () => {
					this.loading = false;
					this.snackBar.open('Errore caricamento partecipanti', 'OK', { duration: 4000 });
				}
			});
	}

	togglePresence(row: IParticipantRow): void {
		if (!this.canEdit || row._updating) return;
		const previous = row.presence;
		const next = previous == 1 ? 0 : 1;
		row.presence = next;
		row._updating = true;
		this.datasetService.post<IParticipantRow>('/dataset/actual_trip_participants/command/update_presence', {
			id: row.id,
			actual_trip_id: this.actualTripId,
			presence: next
		})
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: updated => {
					row._updating = false;
					if (updated && updated.id) {
						const oldId = row.id;
						row.id = updated.id;
						row.last_presence_checked_at = updated.last_presence_checked_at;
						if (this.selection.isSelected(oldId)) {
							this.selection.deselect(oldId);
							this.selection.select(updated.id);
						}
					}
					// il motivo dell'icona dipende dalla presenza: ricalcolo dopo l'update
					if (this.reviewMailSettings.enabled) row._reviewMail = this.buildReviewMailState(row);
					this.presenceChanged.emit();
				},
				error: () => {
					row.presence = previous;
					row._updating = false;
					if (this.reviewMailSettings.enabled) row._reviewMail = this.buildReviewMailState(row);
					this.snackBar.open('Errore aggiornamento presenza', 'OK', { duration: 4000 });
				}
			});
	}

	isAllSelected(): boolean {
		return this.rows.length > 0 && this.selection.selected.length === this.rows.length;
	}

	masterToggle(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
		} else {
			this.rows.forEach(r => this.selection.select(r.id));
		}
	}

	bulkUpdate(presence: 0 | 1): void {
		if (!this.canEdit || this.bulkLoading) return;
		const ids = this.selection.selected;
		if (!ids.length) return;
		this.bulkLoading = true;
		const payload: any = { actual_trip_id: this.actualTripId };
		if (presence === 1) payload.present_participant_ids = ids;
		else payload.absent_participant_ids = ids;
		this.datasetService.post<{ success: boolean; result: { affected: number } }>(
			'/dataset/actual_trip_participants/command/mass_update_presence',
			payload
		)
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe({
				next: res => {
					this.bulkLoading = false;
					this.selection.clear();
					this.snackBar.open(
						`Aggiornati ${res && res.result ? res.result.affected : 0} partecipanti`,
						'OK',
						{ duration: 3000 }
					);
					this.load(true);
					this.presenceChanged.emit();
				},
				error: () => {
					this.bulkLoading = false;
					this.snackBar.open('Errore aggiornamento massivo', 'OK', { duration: 4000 });
				}
			});
	}

	trackById(_index: number, row: IParticipantRow): string {
		return row.id;
	}
}
